import React, { useState, useEffect, useCallback } from 'react';
import { AirPlayButton, ChromecastButton } from './player/CastControls';
import LanguageButton from './player/LanguageButton';
import { ChapterProgress, PlayerStyles, PX, panelStyle, formatTime, sceneAt } from './player/StoryPlayerUI';

const iconBtn = 'pxp-btn inline-flex items-center justify-center rounded-full';
const iconBtnStyle = { width: 36, height: 36, border: 'none', background: 'transparent', color: PX.ink, cursor: 'pointer' };

// The story page's player controls: a slim frosted-white panel floating over
// the bottom of the video, chapter progress with Pip walking along it, and
// the extras.
const InlineVideoControls = ({
  videoRef,
  containerRef,
  subtitleControls = null,
  showSubtitles = false,
  visible = true,
  playback = null,
  castSource = null,
  castTitle = '',
  audio = null,
  onSelectAudio = null,
  dubs = null,
  onDubReady = null,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const sync = () => {
      setIsPlaying(!video.paused);
      setIsMuted(video.muted);
      setCurrentTime(video.currentTime || 0);
      setDuration(video.duration || 0);
    };

    sync();
    const events = ['play', 'pause', 'timeupdate', 'loadedmetadata', 'volumechange'];
    events.forEach((ev) => video.addEventListener(ev, sync));
    return () => events.forEach((ev) => video.removeEventListener(ev, sync));
  }, [videoRef]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const toggleFullscreen = useCallback(() => {
    const target = containerRef?.current || videoRef.current;
    if (!target) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else target.requestFullscreen?.().catch(() => {});
  }, [containerRef, videoRef]);

  const seek = useCallback(
    (t) => {
      const video = videoRef.current;
      if (video) video.currentTime = t;
    },
    [videoRef]
  );

  const scenes = playback?.scenes || [];
  const scene = sceneAt(scenes, currentTime);

  return (
    <div
      className={`absolute inset-x-0 bottom-0 z-20 transition-opacity duration-300 ${
        visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      style={{ padding: 10 }}
      onClick={(e) => e.stopPropagation()}
    >
      <PlayerStyles />
      <div style={{ ...panelStyle, borderRadius: 18, padding: '4px 10px 6px' }}>
        <ChapterProgress
          currentTime={currentTime}
          duration={duration}
          scenes={scenes}
          playback={playback}
          onSeek={seek}
          playing={isPlaying}
          thin
        />
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={togglePlay}
              className="pxp-play inline-flex items-center justify-center rounded-full"
              aria-label={isPlaying ? 'Pause' : 'Play'}
              style={{ width: 34, height: 34, border: 'none', background: PX.blue, color: '#fff', boxShadow: `0 2px 0 ${PX.blueDeep}`, cursor: 'pointer', flexShrink: 0 }}
            >
              {isPlaying ? (
                <svg key="pause" className="w-4 h-4 pxp-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /></svg>
              ) : (
                <svg key="play" className="w-4 h-4 pxp-icon" fill="currentColor" viewBox="0 0 24 24" style={{ marginLeft: 2 }}><path d="M8 5v14l11-7z" /></svg>
              )}
            </button>
            <span style={{ fontSize: 13, fontWeight: 600, color: PX.inkSoft, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            {scene?.title && (
              <span
                className="hidden md:inline"
                style={{ fontSize: 13, fontWeight: 600, color: PX.accentText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}
              >
                · {scene.title}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <LanguageButton
              audio={playback ? audio : null}
              onSelectAudio={onSelectAudio}
              dubs={playback ? dubs : null}
              onDubReady={onDubReady}
              subtitles={showSubtitles ? subtitleControls : null}
            />
            <button type="button" onClick={toggleMute} className={iconBtn} style={iconBtnStyle} aria-label={isMuted ? 'Unmute' : 'Mute'}>
              {isMuted ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" /></svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /></svg>
              )}
            </button>
            {playback && (
              <>
                <AirPlayButton videoRef={videoRef} className={iconBtn} />
                <ChromecastButton videoRef={videoRef} source={castSource} title={castTitle} poster={playback?.poster_url} className={iconBtn} />
              </>
            )}
            <button type="button" onClick={toggleFullscreen} className={iconBtn} style={iconBtnStyle} aria-label="Fullscreen">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InlineVideoControls;
